import type { ReactElement } from "react";
import { useMemo } from "react";

import type { Mutable } from "app/helpers/types";
import {
    LocationAssetType,
    type LocationChipItem,
} from "app/pages/sales-intelligence/common-components/dropdown/LocationDropdown/types";
import type { ChipItem } from "app/pages/sales-intelligence/common-components/filters-panel";
import { LimitedChipItems } from "app/pages/sales-intelligence/common-components/filters-panel";
import { FilterResultContainer } from "app/pages/sales-intelligence/common-components/filters-panel/components/styles";
import { MAX_CHIPS_GROUP_AMOUNT } from "app/pages/sales-intelligence/common-components/filters-panel/constants";
import type { IFilterViewBaseProps } from "app/pages/sales-intelligence/modules/advanced-search-filtering/types/filterInstance";
import { getSelectedCodes } from "app/pages/sales-intelligence/utils/location";
import { buildLocationChips, getCodesAfterLocationChipRemoval } from "app/pages/sales-intelligence/utils/locationChips";
import CountryService from "app/services/CountryService";
import { US_COUNTRY_ID, WORLDWIDE_COUNTRY_ID } from "app/services/countryServiceConst";

import type FindCompanyHeadquarterFilter from "../../../models/CompanyHeadquarterFilter/CompanyHeadquarterFilter";

const CompanyHeadquarterFilterResult = ({
    filter,
    value,
    isExpanded,
    isActionEnabled = true,
    onGetLifecycleStatus,
    onFilterUpdate,
    onFilterValueRemove,
}: IFilterViewBaseProps<FindCompanyHeadquarterFilter>): ReactElement | null => {
    const { regions, countries } = filter.options;

    const onItemDelete = (id: string, item: ChipItem) => {
        const locationItem = item as LocationChipItem;
        if (!value) {
            return;
        }

        const getFilteredArray = <T,>(array: readonly T[] | undefined, itemId: string | number): readonly T[] => {
            return array?.filter((i) => i != itemId) || [];
        };

        const updates: Mutable<Partial<typeof value>> = {};
        const sourceCodes = [...getSelectedCodes(value, regions)];

        switch (locationItem.type) {
            case LocationAssetType.Region: {
                updates.codes = getCodesAfterLocationChipRemoval(locationItem, id, sourceCodes);
                updates.regions = [];
                updates.zip = [];
                break;
            }

            case LocationAssetType.Country: {
                const numericId = Number(id);
                updates.codes = sourceCodes
                    .filter((c) => c != numericId && (numericId != US_COUNTRY_ID || !CountryService.isUSState(c)))
                    .filter((c) => c !== WORLDWIDE_COUNTRY_ID);
                updates.regions = [];
                updates.zip = [];
                break;
            }

            case LocationAssetType.State: {
                updates.codes = getFilteredArray(sourceCodes, id);
                updates.regions = [];
                break;
            }

            case LocationAssetType.Zip: {
                updates.zip = getFilteredArray(value.zip, id);
                break;
            }
        }

        const newValue = { ...value, ...updates };

        onFilterValueRemove?.(newValue);
        onFilterUpdate(newValue);
    };

    const locationChips = useMemo(
        () =>
            buildLocationChips({
                value,
                regions,
                countries,
                excludeCountryIds: new Set([WORLDWIDE_COUNTRY_ID]),
                onGetLifecycleStatus,
            }),
        [value, regions, countries, onGetLifecycleStatus],
    );

    const zipChips: LocationChipItem[] =
        value?.zip?.map((code) => ({
            id: code,
            text: code,
            type: LocationAssetType.Zip,
            chipStatus: onGetLifecycleStatus?.(code),
        })) ?? [];

    const chips: ChipItem[] = [...locationChips, ...zipChips];

    if (!chips.length) {
        return null;
    }

    return (
        <FilterResultContainer>
            <LimitedChipItems
                items={chips}
                maxItems={MAX_CHIPS_GROUP_AMOUNT}
                isTabExpanded={isExpanded}
                hideActionButton={!isActionEnabled}
                onDelete={onItemDelete}
            />
        </FilterResultContainer>
    );
};

export default CompanyHeadquarterFilterResult;
